import { InnerBox } from "@/components/elements/InnerBox";
import { Box, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTalentList } from "@/api/services/main/talent";
import { usePagination } from "@/hooks/usePagination";
import { useSearchParams } from "react-router-dom";
import { TGetTalentListReqDto } from "@/api/services/main/talent/dtos/get-talent-list.dto";
import { useSanitizedSortParams } from "@/hooks/useSanitizedSortParams";
import { useSanitizedPageParam } from "@/hooks/useSanitizedPageParam";
import { CASE_MATCH_SORTABLE_FIELDS } from "@/constants/sortable-fields";
import { START_PAGE } from "@/constants/pagination";
import { Pagination } from "@/components/elements/Pagination";
import { FCC, TOption } from "@/types/common";
import { ERecruitmentStatus } from "@/types/enum";
import { CaseMatchTable } from "@/features/case/components/CaseMatches/CaseMatchTable";
import { getCaseMatchStorageKeys } from "@/features/case/constants/storage-keys";
import { IconFormatListBulleted } from "@/assets/icons";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { Button } from "@/components/elements/Button";
import { useVisible } from "@/hooks/useVisible";
import { CaseMatchFilterConditionPanel } from "@/features/case/components/CaseMatches/CaseMatchFilterConditionPanel";
import { FilterConditionToolbar, TSavedCondition as TToolbarSavedCondition } from "@irohana-dev/components";
import { useDeleteSavedCondition, useSavedConditionList } from "@/api/services/main/saved-condition";
import { readSessionFilterState, writeSessionFilterState } from "@/utils/session-filter-state";
import { applyColumnSettings, enforceLockedColumns, hasDisplayColumnsChanged, resolveSavedConditionColumns } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";
import { black } from "@/theme/colors";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { TCaseMatchDisplayColumnSetting, TCaseMatchFilterFormExtended } from "@/features/case/components/CaseMatches/case-match-filter-shared";
import {
  defaultCaseMatchFilterValues,
  defaultCaseMatchDisplayColumns,
  isEmptyCaseMatchFilterValue,
  normalizeCaseMatchMultiSelectFields,
  stripEmptyCaseMatchFilterFields,
  LOCKED_CASE_MATCH_COLUMN_IDS,
} from "@/features/case/components/CaseMatches/case-match-filter-shared";
import {
  CaseMatchSavedConditionsListModal,
  TCaseMatchSavedCondition,
} from "@/features/case/components/CaseMatches/CaseMatchSavedConditionsListModal";
import { CaseMatchAppliedConditionDetailPopover } from "@/features/case/components/CaseMatches/CaseMatchAppliedConditionDetailPopover";
import { SelectedTalentPagesPopover } from "@/features/talent/components/talent-list/SelectedTalentPagesPopover";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";


type TCaseMatchesProps = {
  companySpecificIndustryId?: number;
  jobId?: number;
  isJobInfoLoading?: boolean;
  pageValidationBasePath?: string;
  /** Unique prefix for localStorage keys. Use "case_create" or "case_edit" to isolate filter state between pages. */
  storageKeyPrefix: string;
  /** Storage key for talent row selection (sessionStorage). */
  talentStorageKey: string;
};

/** Convert popover filter values to backend query params */
const buildQueryFromFilters = (filters: TCaseMatchFilterFormExtended): Partial<TGetTalentListReqDto> => {
  const query: Partial<TGetTalentListReqDto> = {};
  if (filters.talentNumber) query.talentNumber = filters.talentNumber;
  if (filters.fullName) query.fullName = filters.fullName;
  if (filters.nickName) query.nickName = filters.nickName;
  if (filters.hasOtherProposal.length > 0) query.hasOtherProposal = Number(filters.hasOtherProposal[0]);
  if (filters.nationalityId.length > 0) query.nationalityId = filters.nationalityId.map(Number);
  if (filters.gender.length > 0) query.gender = filters.gender.map(Number);
  if (filters.ageFrom || filters.ageTo) {
    const from = filters.ageFrom || "";
    const to = filters.ageTo || "";
    query.age = [`${from}-${to}`];
  }
  if (filters.residenceStatusId.length > 0) query.residenceStatusId = filters.residenceStatusId.map(Number);
  if (filters.periodOfStayFrom) query.periodOfStayFrom = filters.periodOfStayFrom;
  if (filters.periodOfStayTo) query.periodOfStayTo = filters.periodOfStayTo;
  if (filters.languageTestLevelId.length > 0) query.languageTestLevelId = filters.languageTestLevelId.map(Number);
  if (filters.partnerId.length > 0) query.partnerId = filters.partnerId.map(Number);
  if (filters.personInChargeId.length > 0) query.personInChargeId = filters.personInChargeId.map(Number);
  return query;
};

export const CaseMatches: FCC<TCaseMatchesProps> = ({
  companySpecificIndustryId,
  jobId,
  isJobInfoLoading,
  pageValidationBasePath,
  storageKeyPrefix,
  talentStorageKey,
}) => {
  // Derive scoped storage keys and pageType from prefix - prevents collisions between create/edit pages
  const storageKeys = useMemo(() => getCaseMatchStorageKeys(storageKeyPrefix), [storageKeyPrefix]);
  const savedConditionPageType = `${storageKeyPrefix}_match`;

  const pagReq = usePagination();
  const [searchParams, setSearchParams] = useSearchParams();

  useSanitizedPageParam({ basePath: pageValidationBasePath || "" });
  const { sortValue } = useSanitizedSortParams({ allowedFields: [...CASE_MATCH_SORTABLE_FIELDS] });

  // Advanced filter state - restore from localStorage
  const [advancedFilters, setAdvancedFilters] = useState<TCaseMatchFilterFormExtended | null>(() => {
    const saved = localStorage.getItem(storageKeys.advancedFilters);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...defaultCaseMatchFilterValues,
          ...normalizeCaseMatchMultiSelectFields(parsed as Partial<Record<string, unknown>>),
        } as TCaseMatchFilterFormExtended;
      } catch {
        return null;
      }
    }
    return null;
  });
  const [displayColumns, setDisplayColumns] = useState<TCaseMatchDisplayColumnSetting[]>(() => {
    const stored = localStorage.getItem(storageKeys.displayColumns);
    if (stored) {
      try {
        return enforceLockedColumns(JSON.parse(stored), LOCKED_CASE_MATCH_COLUMN_IDS);
      } catch {
        /* ignore */
      }
    }
    return [...defaultCaseMatchDisplayColumns];
  });

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

  // Saved condition state - restore from localStorage
  const savedConditionsModal = useVisible();
  const [filterMode, setFilterMode] = useState<"create" | "edit">("create");
  const { data: savedConditionsData } = useSavedConditionList(savedConditionPageType);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const deleteSavedCondition = useDeleteSavedCondition();
  const [appliedConditionName, setAppliedConditionName] = useState<string>(
    () => localStorage.getItem(storageKeys.appliedConditionName) ?? "",
  );
  const [appliedConditionId, setAppliedConditionId] = useState<number | null>(() => {
    const saved = localStorage.getItem(storageKeys.appliedConditionId);
    if (saved) {
      const parsed = Number(saved);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  });
  const [conditionDetailAnchorEl, setConditionDetailAnchorEl] = useState<HTMLElement | null>(null);

  const [selectionUpdateTrigger, setSelectionUpdateTrigger] = useState<number>(0);

  const getTalentSelected = useCallback((): number[] => {
    const raw = sessionStorage.getItem(talentStorageKey);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      return Object.values(parsed).flat() as number[];
    } catch {
      return [];
    }
  }, [talentStorageKey]);

  const selectedCount = useMemo(() => {
    return getTalentSelected().length;
  }, [getTalentSelected, selectionUpdateTrigger]);

  // Build API query from advanced filters
  const filterQueryParams = useMemo(() => (advancedFilters ? buildQueryFromFilters(advancedFilters) : {}), [advancedFilters]);

  const query: TGetTalentListReqDto = useMemo(
    () => ({
      page: pagReq.page || START_PAGE,
      perPage: 10,
      recruitmentStatus: [ERecruitmentStatus.JOB_SEEKING],
      isCase: 1,
      companySpecificIndustryId: companySpecificIndustryId ? [companySpecificIndustryId] : undefined,
      jobId,
      ...filterQueryParams,
      sorts: sortValue ? [sortValue as `${string}|${string}`] : undefined,
    }),
    [pagReq.page, companySpecificIndustryId, jobId, filterQueryParams, sortValue],
  );

  const { data: talentResponse, isLoading: isLoadingTalents } = useTalentList(query, !isJobInfoLoading);

  // Separate unfiltered query for stable autocomplete suggestions
  // so that talentNumber / fullName / nickName options are not affected by active filters
  const unfilteredQuery: TGetTalentListReqDto = useMemo(() => ({
    page: 1,
    perPage: 500,
    recruitmentStatus: [ERecruitmentStatus.JOB_SEEKING],
    isCase: 1,
    companySpecificIndustryId: companySpecificIndustryId ? [companySpecificIndustryId] : undefined,
    jobId,
  }), [companySpecificIndustryId, jobId]);
  const { data: unfilteredResponse } = useTalentList(unfilteredQuery, !isJobInfoLoading);

  const unfilteredListOptions = useMemo(() => {
    const talents = unfilteredResponse?.data ?? [];
    const talentNumberSet = new Set<string>();
    const fullNameSet = new Set<string>();
    const nickNameSet = new Set<string>();
    talents.forEach((t) => {
      if (t.talentNumber) talentNumberSet.add(t.talentNumber);
      if (t.fullName) fullNameSet.add(t.fullName);
      if (t.nickName) nickNameSet.add(t.nickName);
    });
    const toSortedOptions = (set: Set<string>): TOption[] =>
      Array.from(set).sort().map((v) => ({ label: v, value: v }));
    return {
      talentNumber: toSortedOptions(talentNumberSet),
      fullName: toSortedOptions(fullNameSet),
      nickName: toSortedOptions(nickNameSet),
    };
  }, [unfilteredResponse?.data]);

  const navigateFirstPage = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.set("page", "1");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleApplyAdvancedFilters = useCallback(
    (filters: TCaseMatchFilterFormExtended) => {
      const hasAnyFilter = Object.values(filters).some((v) => !isEmptyCaseMatchFilterValue(v));
      if (hasAnyFilter) {
        setAdvancedFilters(filters);
        localStorage.setItem(storageKeys.advancedFilters, JSON.stringify(stripEmptyCaseMatchFilterFields(filters)));
      } else {
        setAdvancedFilters(null);
        localStorage.removeItem(storageKeys.advancedFilters);
      }
      navigateFirstPage();
    },
    [navigateFirstPage, storageKeys],
  );

  const handleApplyDisplaySettings = useCallback(
    (columns: TCaseMatchDisplayColumnSetting[]) => {
      setDisplayColumns(enforceLockedColumns(columns, LOCKED_CASE_MATCH_COLUMN_IDS));
      localStorage.setItem(storageKeys.displayColumns, JSON.stringify(columns));
    },
    [storageKeys],
  );

  const handleClearAppliedFilter = useCallback(() => {
    setAdvancedFilters(null);
    setAppliedConditionName("");
    setAppliedConditionId(null);
    handleApplyDisplaySettings([...defaultCaseMatchDisplayColumns]);
    setIsClearConfirmOpen(false);
    localStorage.removeItem(storageKeys.advancedFilters);
    localStorage.removeItem(storageKeys.appliedConditionName);
    localStorage.removeItem(storageKeys.appliedConditionId);
  }, [storageKeys, handleApplyDisplaySettings]);

  const handleApplySavedCondition = useCallback(
    (condition: TCaseMatchSavedCondition) => {
      const normalized = {
        ...defaultCaseMatchFilterValues,
        ...normalizeCaseMatchMultiSelectFields(condition.filters as unknown as Partial<Record<string, unknown>>),
      };
      handleApplyAdvancedFilters(normalized);
      setAppliedConditionName(condition.title);
      setAppliedConditionId(condition.id);
      localStorage.setItem(storageKeys.appliedConditionName, condition.title);
      localStorage.setItem(storageKeys.appliedConditionId, String(condition.id));
    },
    [handleApplyAdvancedFilters, storageKeys],
  );

  const hasSeededDefault = useRef(readSessionFilterState<boolean>(storageKeys.defaultSeeded) ?? false);

  useEffect(() => {
    if (hasSeededDefault.current || !savedConditionsData) return;
    hasSeededDefault.current = true;
    writeSessionFilterState(storageKeys.defaultSeeded, true);

    const fallback = savedConditionsData.data.find((item) => item.isDefault);
    if (!fallback) return;

    handleApplySavedCondition({
      id: Number(fallback.id),
      title: fallback.title,
      filters: fallback.filters,
    } as TCaseMatchSavedCondition);
    handleApplyDisplaySettings(resolveSavedConditionColumns(fallback, defaultCaseMatchDisplayColumns));
  }, [savedConditionsData]);

  const handleConditionTitleChange = useCallback(
    (_id: number, newTitle: string) => {
      setAppliedConditionName(newTitle);
      localStorage.setItem(storageKeys.appliedConditionName, newTitle);
    },
    [storageKeys],
  );

  const justSavedConditionRef = useRef(false);

  const handleApplyFiltersFromPanel = useCallback(
    (filters: TCaseMatchFilterFormExtended) => {
      handleApplyAdvancedFilters(filters);
      if (justSavedConditionRef.current) {
        justSavedConditionRef.current = false;
        return;
      }
      if (filterMode === "create") {
        setAppliedConditionName("");
        setAppliedConditionId(null);
        localStorage.removeItem(storageKeys.appliedConditionName);
        localStorage.removeItem(storageKeys.appliedConditionId);
      }
    },
    [handleApplyAdvancedFilters, filterMode, storageKeys],
  );

  const handleConditionSaved = useCallback(
    (id: number, title: string) => {
      justSavedConditionRef.current = true;
      setAppliedConditionName(title);
      setAppliedConditionId(id);
      localStorage.setItem(storageKeys.appliedConditionName, title);
      localStorage.setItem(storageKeys.appliedConditionId, String(id));
      setFilterMode("edit");
    },
    [storageKeys],
  );

  const appliedCondition =
    filterMode === "edit" && appliedConditionId
      ? savedConditionsData?.data.find((item) => Number(item.id) === Number(appliedConditionId))
      : undefined;

  const toolbarConditions = useMemo<TToolbarSavedCondition[]>(
    () =>
      (savedConditionsData?.data ?? []).map((item) => ({
        id: Number(item.id),
        name: item.title,
        shareScope: item.shareScope ?? "self",
        isDefault: item.isDefault ?? false,
        badgeColor: item.defaultColor ?? undefined,
      })),
    [savedConditionsData],
  );

  const handleSelectConditionFromToolbar = useCallback(
    (id: string | number) => {
      const picked = savedConditionsData?.data.find((item) => Number(item.id) === Number(id));
      if (!picked) return;
      handleApplySavedCondition({
        id: Number(picked.id),
        title: picked.title,
        filters: picked.filters,
      } as TCaseMatchSavedCondition);
      if (picked.includeColumnSettings && picked.columnSettings) {
        const settings = picked.columnSettings as { order?: string[]; hidden?: string[] };
        handleApplyDisplaySettings(
          applyColumnSettings(displayColumns, settings.order ?? [], settings.hidden ?? [], defaultCaseMatchDisplayColumns),
        );
      }
    },
    [savedConditionsData, handleApplySavedCondition, handleApplyDisplaySettings, displayColumns],
  );

  const handleDeleteAppliedCondition = useCallback(async () => {
    if (!appliedConditionId) return;
    try {
      await deleteSavedCondition.mutateAsync(appliedConditionId);
      toast.success("条件を削除しました。");
      handleClearAppliedFilter();
    } catch (e) {
      showError(e);
    } finally {
      setIsDeleteConfirmOpen(false);
    }
  }, [appliedConditionId, deleteSavedCondition, handleClearAppliedFilter]);

  const activeFilterCount = useMemo(() => {
    if (!advancedFilters) return 0;
    const { periodOfStayFrom, periodOfStayTo, ageFrom, ageTo, ...rest } = advancedFilters;
    let count = Object.values(rest).filter((v) => !isEmptyCaseMatchFilterValue(v)).length;
    if (periodOfStayFrom || periodOfStayTo) count += 1;
    if (ageFrom || ageTo) count += 1;
    return count;
  }, [advancedFilters]);

  const formatTotal = (total: number = 0) => total.toLocaleString("en-US");

  return (
    <InnerBox mt={3} showDivider={false} title={<Typography variant="head20Bold">人材の選択</Typography>}>
      {/* Filter top bar - same layout as JobListTop */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {/* Left side: count + filter button */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="body14Semi">
            全 {formatTotal(talentResponse?.pagination?.total) ?? 0} 件中{" "}
            {Math.min(
              (talentResponse?.pagination?.page ?? 1) * (talentResponse?.pagination?.perPage ?? 10),
              talentResponse?.pagination?.total ?? 0,
            )}{" "}
            件を表示
          </Typography>
          <Typography variant="body14Semi">│</Typography>
          <SelectedTalentPagesPopover
            selectedCount={selectedCount}
            query={query}
            getTalentSelected={getTalentSelected}
            isSelectAll={false}
            excludedIds={[]}
          />

          <FilterConditionToolbar
            onOpenFilter={() => {
              setFilterMode("create");
              setFilterAnchorEl(document.body);
            }}
            activeCount={
              activeFilterCount || Number(Boolean(appliedConditionId) || hasDisplayColumnsChanged(displayColumns, defaultCaseMatchDisplayColumns))
            }
            conditions={toolbarConditions}
            selectedId={appliedConditionId ?? undefined}
            onSelect={handleSelectConditionFromToolbar}
            onClearSelection={() => setIsClearConfirmOpen(true)}
            onEdit={() => {
              setFilterMode("edit");
              setFilterAnchorEl(document.body);
            }}
            onDelete={() => setIsDeleteConfirmOpen(true)}
            shareColor={filterConditionShareColor}
          />

          {appliedConditionName && (
            <Box
              component="span"
              onClick={(e: React.MouseEvent<HTMLElement>) => setConditionDetailAnchorEl(e.currentTarget)}
              sx={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
            >
              <InfoOutlinedIcon sx={{ color: black[80], fontSize: 16 }} />
            </Box>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Button type="button" variant="outline" size="sm" startIcon={<IconFormatListBulleted width={20} height={20} />} onClick={savedConditionsModal.open}>
            保存した条件一覧
          </Button>
        </Stack>
      </Box>

      <CaseMatchTable
        rows={talentResponse?.data ?? []}
        page={talentResponse?.pagination?.page ?? 0}
        isLoading={isLoadingTalents}
        storageKey={talentStorageKey}
        displayColumns={displayColumns}
        onSelectionChange={() => setSelectionUpdateTrigger((prev) => prev + 1)}
      />

      <Stack mt={3}>
        <Pagination totalPage={talentResponse?.pagination?.totalPage ?? 1} />
      </Stack>

      {/* Filter popover */}
      <CaseMatchFilterConditionPanel
        isOpen={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        mode={filterMode}
        onApplyFilters={handleApplyFiltersFromPanel}
        onApplyDisplaySettings={handleApplyDisplaySettings}
        initialFilters={filterMode === "edit" ? advancedFilters || undefined : defaultCaseMatchFilterValues}
        initialDisplayColumns={displayColumns}
        columnsStorageKey={storageKeys.displayColumns}
        pageType={savedConditionPageType}
        appliedConditionId={filterMode === "edit" ? appliedConditionId : null}
        appliedConditionName={filterMode === "edit" ? appliedConditionName : ""}
        appliedConditionShareScope={appliedCondition?.shareScope}
        appliedConditionIsDefault={appliedCondition?.isDefault}
        appliedConditionIncludeColumnSettings={appliedCondition?.includeColumnSettings}
        appliedConditionColumnSettings={appliedCondition?.columnSettings as { order?: string[]; hidden?: string[] } | null | undefined}
        onConditionSaved={handleConditionSaved}
        talentNumberOptions={unfilteredListOptions.talentNumber}
        fullNameOptions={unfilteredListOptions.fullName}
        nickNameOptions={unfilteredListOptions.nickName}
      />

      {/* Saved conditions list modal */}
      <CaseMatchSavedConditionsListModal
        isOpen={savedConditionsModal.visible}
        onClose={savedConditionsModal.close}
        onApply={handleApplySavedCondition}
        pageType={savedConditionPageType}
        appliedConditionId={appliedConditionId}
        onConditionTitleChange={handleConditionTitleChange}
      />

      {/* Applied condition detail popover */}
      <CaseMatchAppliedConditionDetailPopover
        anchorEl={conditionDetailAnchorEl}
        onClose={() => setConditionDetailAnchorEl(null)}
        filterValues={advancedFilters ?? null}
      />

      <ConfirmDeleteModal
        open={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onOk={handleClearAppliedFilter}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
      <ConfirmDeleteModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onOk={handleDeleteAppliedCondition}
        message="この条件を削除してもよろしいですか？"
        title=""
      />
    </InnerBox>
  );
};
